import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wallet,
  Plus,
  Copy,
  Check,
  AlertCircle,
  RefreshCw,
  ArrowLeft,
  QrCode,
  Eye,
  EyeOff,
  ExternalLink,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useAuthStore } from '../../stores/auth.store';
import {
  useCryptoWallets,
  useCreateWallet,
  usePrimaryVirtualAccount,
  CryptoWallet,
  CryptoNetwork,
} from '../../hooks/useCrypto';

const NETWORK_OPTIONS = [
  { value: 'ETHEREUM', label: 'Ethereum (ETH)' },
  { value: 'BSC', label: 'BNB Smart Chain (BSC)' },
  { value: 'POLYGON', label: 'Polygon (MATIC)' },
  { value: 'BITCOIN', label: 'Bitcoin (BTC)' },
  { value: 'SOLANA', label: 'Solana (SOL)' },
];

const getNetworkIcon = (network: string): string => {
  switch (network) {
    case 'ETHEREUM': return '⟠';
    case 'BSC': return '🔶';
    case 'POLYGON': return '🟣';
    case 'BITCOIN': return '₿';
    case 'SOLANA': return '◎';
    default: return '💰';
  }
};

const getNetworkColor = (network: string): string => {
  switch (network) {
    case 'ETHEREUM': return 'from-blue-500 to-purple-500';
    case 'BSC': return 'from-yellow-500 to-orange-500';
    case 'POLYGON': return 'from-purple-500 to-pink-500';
    case 'BITCOIN': return 'from-orange-500 to-yellow-500';
    case 'SOLANA': return 'from-purple-500 to-green-500';
    default: return 'from-surface-500 to-surface-600';
  }
};

const getExplorerUrl = (network: string, address: string): string => {
  switch (network) {
    case 'ETHEREUM': return `https://etherscan.io/address/${address}`;
    case 'BSC': return `https://bscscan.com/address/${address}`;
    case 'POLYGON': return `https://polygonscan.com/address/${address}`;
    case 'BITCOIN': return `https://blockchain.com/btc/address/${address}`;
    case 'SOLANA': return `https://solscan.io/account/${address}`;
    default: return '#';
  }
};

export function BankingCryptoWallets() {
  const navigate = useNavigate();
  const { hasCategory } = useAuthStore();
  
  // Get primary virtual account ID from banking API
  const { data: primaryAccount, isLoading: accountLoading } = usePrimaryVirtualAccount();
  const virtualAccountId = primaryAccount?.id || '';

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState<CryptoNetwork>('ETHEREUM');
  const [walletLabel, setWalletLabel] = useState('');
  const [showQrModal, setShowQrModal] = useState<CryptoWallet | null>(null);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [networkFilter, setNetworkFilter] = useState<string>('');

  const { data: wallets = [], isLoading: walletsLoading, refetch } = useCryptoWallets(virtualAccountId);
  const createWalletMutation = useCreateWallet(virtualAccountId);
  
  const isLoading = accountLoading || walletsLoading;

  // Filter wallets by network
  const filteredWallets = networkFilter 
    ? wallets.filter(w => w.network === networkFilter)
    : wallets;

  // Check if crypto is enabled
  if (!hasCategory('CRYPTO')) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            Crypto não habilitado
          </h2>
          <p className="text-surface-400">
            Esta funcionalidade não está disponível para sua conta.
          </p>
        </Card>
      </div>
    );
  }
  
  // Check if user has a virtual account
  if (!accountLoading && !virtualAccountId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            Conta não encontrada
          </h2>
          <p className="text-surface-400">
            Você precisa ter uma conta virtual para acessar funcionalidades crypto.
          </p>
        </Card>
      </div>
    );
  }

  const handleCreateWallet = async () => {
    try {
      await createWalletMutation.mutateAsync({
        network: selectedNetwork,
        label: walletLabel || undefined,
      });
      setShowCreateModal(false);
      setWalletLabel('');
      refetch();
    } catch (error) {
      console.error('Error creating wallet:', error);
    }
  };

  const copyToClipboard = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  // Calculate total balance
  const totalBalanceUsd = filteredWallets.reduce((sum, w) => sum + (w.balanceUsd || 0), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/banking/crypto')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
            <Wallet className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-100">Minhas Carteiras</h1>
            <p className="text-surface-400">Gerencie suas carteiras crypto</p>
          </div>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Carteira
        </Button>
      </div>

      {/* Total Balance */}
      <Card className="bg-gradient-to-br from-orange-900/30 to-yellow-900/30 border-orange-500/30">
        <div className="text-center">
          <p className="text-surface-400 text-sm mb-2">Saldo Total em Carteiras</p>
          <p className="text-4xl font-bold text-surface-100">
            ${totalBalanceUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-surface-500 text-sm mt-1">
            {filteredWallets.length} carteira{filteredWallets.length !== 1 ? 's' : ''} {networkFilter ? `(${networkFilter})` : 'ativa' + (filteredWallets.length !== 1 ? 's' : '')}
          </p>
        </div>
      </Card>

      {/* Network Filter */}
      {wallets.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={networkFilter === '' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setNetworkFilter('')}
          >
            Todas ({wallets.length})
          </Button>
          {NETWORK_OPTIONS.map((network) => {
            const count = wallets.filter(w => w.network === network.value).length;
            if (count === 0) return null;
            return (
              <Button
                key={network.value}
                variant={networkFilter === network.value ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setNetworkFilter(network.value)}
              >
                {getNetworkIcon(network.value)} {network.value} ({count})
              </Button>
            );
          })}
        </div>
      )}

      {/* Wallets Grid */}
      {filteredWallets.length === 0 ? (
        <Card className="text-center py-12">
          <Wallet className="w-12 h-12 mx-auto mb-4 text-surface-500" />
          <h3 className="text-lg font-medium text-surface-100 mb-2">
            {networkFilter ? `Nenhuma carteira ${networkFilter}` : 'Nenhuma carteira encontrada'}
          </h3>
          <p className="text-surface-400 mb-4">
            {networkFilter 
              ? `Crie uma carteira na rede ${networkFilter}` 
              : 'Crie sua primeira carteira para começar a receber criptomoedas'}
          </p>
          <Button onClick={() => {
            if (networkFilter) setSelectedNetwork(networkFilter as CryptoNetwork);
            setShowCreateModal(true);
          }}>
            <Plus className="w-4 h-4 mr-2" />
            Criar Carteira {networkFilter || ''}
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredWallets.map((wallet) => (
            <WalletCard
              key={wallet.id}
              wallet={wallet}
              onCopy={copyToClipboard}
              onShowQr={() => setShowQrModal(wallet)}
              isCopied={copiedAddress === wallet.address}
            />
          ))}
        </div>
      )}

      {/* Create Wallet Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Criar Nova Carteira"
      >
        <div className="space-y-6">
          <Select
            label="Rede"
            options={NETWORK_OPTIONS}
            value={selectedNetwork}
            onChange={(e) => setSelectedNetwork(e.target.value as CryptoNetwork)}
          />

          <Input
            label="Nome da Carteira (opcional)"
            placeholder="Ex: Carteira Principal"
            value={walletLabel}
            onChange={(e) => setWalletLabel(e.target.value)}
          />

          {/* Warning */}
          <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-400 font-medium">Importante</p>
                <p className="text-surface-400 text-sm">
                  A chave privada será gerada localmente e exibida apenas uma vez. 
                  Guarde-a em um local seguro. Sem ela, você não poderá recuperar seus fundos.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateWallet}
              disabled={createWalletMutation.isPending}
            >
              {createWalletMutation.isPending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                'Criar Carteira'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* QR Code Modal */}
      <Modal
        isOpen={!!showQrModal}
        onClose={() => setShowQrModal(null)}
        title="Receber Crypto"
      >
        {showQrModal && (
          <div className="space-y-6 text-center">
            <div className="flex justify-center">
              <div className="w-48 h-48 bg-white rounded-xl p-4 flex items-center justify-center">
                <QrCode className="w-full h-full text-surface-900" />
              </div>
            </div>
            
            <div>
              <p className="text-surface-400 text-sm mb-2">Endereço da Carteira</p>
              <div className="flex items-center gap-2 justify-center">
                <code className="text-sm text-surface-100 bg-surface-800 px-3 py-2 rounded-lg break-all">
                  {showQrModal.address}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(showQrModal.address)}
                >
                  {copiedAddress === showQrModal.address ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-surface-800/50">
              <p className="text-surface-400 text-sm">
                Envie apenas <span className="text-primary-400 font-medium">{showQrModal.network}</span> para este endereço.
                Enviar outras moedas pode resultar em perda permanente.
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// WalletCard Component
interface WalletCardProps {
  wallet: CryptoWallet;
  onCopy: (address: string) => void;
  onShowQr: () => void;
  isCopied: boolean;
}

function WalletCard({ wallet, onCopy, onShowQr, isCopied }: WalletCardProps) {
  const [showBalance, setShowBalance] = useState(true);

  return (
    <Card className="hover:border-primary-500/50 transition-all duration-300">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getNetworkColor(wallet.network)} flex items-center justify-center text-2xl`}>
            {getNetworkIcon(wallet.network)}
          </div>
          <div>
            <h3 className="font-semibold text-surface-100">
              {wallet.label || `Carteira ${wallet.network}`}
            </h3>
            <p className="text-surface-400 text-sm">{wallet.network}</p>
          </div>
        </div>
        <Badge className="bg-green-500/20 text-green-400">Ativa</Badge>
      </div>

      {/* Balance */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <p className="text-surface-500 text-sm">Saldo</p>
          <button onClick={() => setShowBalance(!showBalance)} className="text-surface-400 hover:text-surface-100">
            {showBalance ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex items-baseline gap-2">
          <p className="text-2xl font-bold text-surface-100">
            {showBalance ? (
              `${wallet.balance?.toFixed(6) || '0.000000'} ${wallet.network}`
            ) : (
              '••••••'
            )}
          </p>
        </div>
        {showBalance && wallet.balanceUsd !== undefined && (
          <p className="text-surface-400 text-sm">
            ≈ ${wallet.balanceUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        )}
      </div>

      {/* Address */}
      <div className="mb-4 p-3 rounded-lg bg-surface-800/50">
        <p className="text-surface-500 text-xs mb-1">Endereço</p>
        <div className="flex items-center gap-2">
          <code className="text-xs text-surface-300 truncate flex-1">
            {wallet.address}
          </code>
          <button
            onClick={() => onCopy(wallet.address)}
            className="text-surface-400 hover:text-primary-400 transition-colors"
          >
            {isCopied ? (
              <Check className="w-4 h-4 text-green-400" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button variant="secondary" className="flex-1" onClick={onShowQr}>
          <QrCode className="w-4 h-4 mr-2" />
          Receber
        </Button>
        <Button
          variant="ghost"
          onClick={() => window.open(getExplorerUrl(wallet.network, wallet.address), '_blank')}
        >
          <ExternalLink className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
}
